/* Pearls of Haya — theme.js (vanilla, no dependencies) */
(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopMQ = window.matchMedia('(min-width: 990px)');
  const tabletMQ = window.matchMedia('(min-width: 750px)');

  /* ---------------------------------------------------------------------
     Toast (used for cart errors that have no inline slot)
     --------------------------------------------------------------------- */
  const toast = (message) => {
    let el = $('[data-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'toast';
      el.setAttribute('data-toast', '');
      el.setAttribute('role', 'alert');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 4500);
  };

  /* ---------------------------------------------------------------------
     Drawers
     --------------------------------------------------------------------- */
  const Drawer = {
    active: null,
    trigger: null,
    open(drawer, trigger) {
      if (!drawer) return;
      if (this.active && this.active !== drawer) this.close(this.active, false);
      this.active = drawer;
      this.trigger = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      $$(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      const focusTarget = $('[data-autofocus]', drawer) || $('.drawer__panel', drawer);
      setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 80);
    },
    close(drawer = this.active, restoreFocus = true) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      if (!drawer.hasAttribute('data-drawer-static')) drawer.setAttribute('aria-hidden', 'true');
      $$(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      if (this.active === drawer) this.active = null;
      if (!this.active) document.body.classList.remove('is-locked');
      if (restoreFocus && this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      Drawer.open(document.getElementById(opener.dataset.drawerOpen), opener);
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      const drawer = e.target.closest('[data-drawer]');
      if (drawer && drawer.classList.contains('is-open')) {
        e.preventDefault();
        Drawer.close(drawer);
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (Drawer.active) Drawer.close();
      $$('details[data-hover-details][open]').forEach((d) => d.removeAttribute('open'));
    }
    if (e.key === 'Tab' && Drawer.active) {
      const focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])', Drawer.active)
        .filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* Filters render as a static sidebar on desktop: expose them to assistive tech there */
  const syncStaticDrawers = () => {
    $$('.collection__layout--sidebar .facets-drawer').forEach((drawer) => {
      const panel = $('.drawer__panel', drawer);
      if (desktopMQ.matches) {
        if (drawer === Drawer.active) Drawer.close(drawer, false);
        drawer.setAttribute('data-drawer-static', '');
        drawer.removeAttribute('aria-hidden');
        panel?.removeAttribute('role');
        panel?.removeAttribute('aria-modal');
      } else {
        drawer.removeAttribute('data-drawer-static');
        if (!drawer.classList.contains('is-open')) drawer.setAttribute('aria-hidden', 'true');
        panel?.setAttribute('role', 'dialog');
        panel?.setAttribute('aria-modal', 'true');
      }
    });
  };
  syncStaticDrawers();
  desktopMQ.addEventListener('change', syncStaticDrawers);

  /* ---------------------------------------------------------------------
     Header: sticky state, offset variable, hover dropdowns
     --------------------------------------------------------------------- */
  const setHeaderOffset = () => {
    const announcement = $('.section-announcement');
    const headerSection = $('.section-header');
    const h = (announcement ? announcement.offsetHeight : 0) + (headerSection ? headerSection.offsetHeight : 0);
    document.documentElement.style.setProperty('--header-offset', `${h}px`);
  };
  setHeaderOffset();
  window.addEventListener('resize', debounce(setHeaderOffset, 150));

  window.addEventListener('scroll', () => {
    const header = $('[data-header]');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 10);
  }, { passive: true });

  const hoverMQ = window.matchMedia('(hover: hover) and (min-width: 990px)');
  const initHoverMenus = (root = document) => {
    $$('details[data-hover-details]', root).forEach((details) => {
      if (details.dataset.ready) return;
      details.dataset.ready = 'true';
      let timer;
      const item = details.closest('.nav__item') || details;
      item.addEventListener('mouseenter', () => {
        if (!hoverMQ.matches) return;
        clearTimeout(timer);
        $$('details[data-hover-details][open]').forEach((d) => d !== details && d.removeAttribute('open'));
        details.setAttribute('open', '');
      });
      item.addEventListener('mouseleave', () => {
        if (!hoverMQ.matches) return;
        timer = setTimeout(() => details.removeAttribute('open'), 160);
      });
    });
  };
  initHoverMenus();
  document.addEventListener('click', (e) => {
    $$('details[data-hover-details][open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
  });

  /* ---------------------------------------------------------------------
     Footer accordions: collapsible on mobile only
     --------------------------------------------------------------------- */
  const syncFooterAccordions = () => {
    $$('.footer-block__collapsible').forEach((d) => {
      if (tabletMQ.matches) d.setAttribute('open', '');
      else if (!d.dataset.touched) d.removeAttribute('open');
    });
  };
  syncFooterAccordions();
  tabletMQ.addEventListener('change', syncFooterAccordions);
  document.addEventListener('click', (e) => {
    const summary = e.target.closest('.footer-block__collapsible > summary');
    if (!summary) return;
    if (tabletMQ.matches) e.preventDefault();
    else summary.parentElement.dataset.touched = 'true';
  });

  /* ---------------------------------------------------------------------
     Announcement bar rotation
     --------------------------------------------------------------------- */
  const initAnnouncements = (root = document) => {
    $$('[data-announcement]', root).forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = 'true';
      const items = $$('.announcement__item', bar);
      if (items.length < 2) return;
      let index = 0;
      let timer;
      const speed = (parseInt(bar.dataset.speed, 10) || 5) * 1000;
      const show = (i) => {
        items[index].classList.remove('is-active');
        index = (i + items.length) % items.length;
        items[index].classList.add('is-active');
      };
      const stop = () => clearInterval(timer);
      const play = () => {
        stop();
        if (reducedMotion.matches || !document.body.contains(bar)) return;
        timer = setInterval(() => (document.body.contains(bar) ? show(index + 1) : stop()), speed);
      };
      $('[data-announcement-next]', bar)?.addEventListener('click', () => { show(index + 1); play(); });
      $('[data-announcement-prev]', bar)?.addEventListener('click', () => { show(index - 1); play(); });
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', play);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', play);
      play();
    });
  };
  initAnnouncements();

  /* ---------------------------------------------------------------------
     Reveal on scroll (content is only hidden once this script is running)
     --------------------------------------------------------------------- */
  const revealEnabled = document.body.classList.contains('reveal-enabled') && !reducedMotion.matches && 'IntersectionObserver' in window;
  const revealObserver = revealEnabled ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  const initReveal = (root = document) => {
    if (!revealObserver) return;
    $$('[data-reveal]:not(.is-visible)', root).forEach((el) => revealObserver.observe(el));
  };
  if (revealEnabled) document.body.classList.add('reveal-ready');
  initReveal();

  /* ---------------------------------------------------------------------
     Sliders (testimonials)
     --------------------------------------------------------------------- */
  const initSliders = (root = document) => {
    $$('[data-slider]', root).forEach((slider) => {
      const track = $('[data-slider-track]', slider);
      if (!track || track.dataset.ready) return;
      track.dataset.ready = 'true';
      const prev = $('[data-slider-prev]', slider);
      const next = $('[data-slider-next]', slider);
      const progress = $('[data-slider-progress]', slider);
      const step = () => {
        const item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 4;
        if (next) next.disabled = track.scrollLeft >= max - 4;
        if (progress) {
          const visible = Math.max(track.clientWidth / track.scrollWidth, 0.12);
          progress.style.width = `${visible * 100}%`;
          progress.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * ((1 / visible) - 1) * 100 : 0}%)`;
        }
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', debounce(update, 40), { passive: true });
      window.addEventListener('resize', debounce(update, 150));
      update();
    });
  };
  initSliders();

  /* ---------------------------------------------------------------------
     Cart
     --------------------------------------------------------------------- */
  const getCartDrawer = () => document.getElementById('CartDrawer');
  const drawerMode = () => theme.cartType === 'drawer' && !!getCartDrawer();

  const updateCartCount = (count) => {
    if (count === undefined || count === null) return;
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  };

  const cartSectionIds = () => {
    const ids = $$('[data-cart-section]').map((el) => el.dataset.cartSection);
    if (getCartDrawer()) ids.push('cart-drawer');
    return ids;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      if (id === 'cart-drawer') {
        const fresh = $('[data-cart-drawer-inner]', doc);
        const drawer = getCartDrawer();
        const current = drawer && $('[data-cart-drawer-inner]', drawer);
        if (fresh && current) {
          current.replaceWith(fresh);
          updateCartCount(fresh.dataset.count);
        }
      } else {
        const fresh = $(`[data-cart-section="${id}"]`, doc);
        const current = $(`[data-cart-section="${id}"]`);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          updateCartCount(fresh.dataset.count);
        }
      }
    });
    initReveal();
  };

  const openCart = (trigger) => { if (drawerMode()) Drawer.open(getCartDrawer(), trigger); };

  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-open]');
    if (!link || !drawerMode() || document.body.classList.contains('template-cart')) return;
    e.preventDefault();
    openCart(link);
  });

  // Add to cart (product form, quick add, upsell)
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-product-form]');
    if (!form) return;

    const button = $('[data-add-button]', form) || $('[type="submit"]', form);
    if (button && (button.disabled || button.getAttribute('aria-disabled') === 'true')) {
      e.preventDefault();
      return;
    }
    if (!drawerMode()) return; // native submit → /cart

    e.preventDefault();
    const errorEl = form.closest('[data-product]')?.querySelector('[data-form-error]');
    const showError = (message) => {
      if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
      else toast(message);
    };
    button?.classList.add('is-loading');
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    const body = new FormData(form);
    body.append('sections', cartSectionIds().join(','));
    body.append('sections_url', window.location.pathname);

    try {
      const res = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        showError(data.description || data.message || theme.strings.cartError);
        return;
      }
      renderCartSections(data.sections);
      button?.classList.add('is-added');
      setTimeout(() => button?.classList.remove('is-added'), 1800);
      openCart(button);
    } catch (err) {
      showError(theme.strings.cartError);
    } finally {
      button?.classList.remove('is-loading');
    }
  });

  // Change quantities / remove items
  const changeLine = async (line, quantity, itemEl) => {
    itemEl?.classList.add('is-updating');
    try {
      const res = await fetch(theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        itemEl?.classList.remove('is-updating');
        const input = itemEl && $('[data-cart-line-input]', itemEl);
        if (input && input.dataset.value) input.value = input.dataset.value;
        const err = itemEl?.querySelector('[data-line-error]');
        const message = data.description || data.message || theme.strings.cartError;
        if (err) { err.textContent = message; err.hidden = false; } else toast(message);
        return;
      }
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
    } catch (err) {
      window.location.reload();
    }
  };

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(Number(remove.dataset.line), 0, remove.closest('.cart-item'));
      return;
    }
    const qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      const item = qtyBtn.closest('.cart-item');
      const input = $('[data-cart-line-input]', item);
      const current = parseInt(input.value, 10) || 0;
      input.dataset.value = current;
      const nextQty = Math.max(0, current + (qtyBtn.dataset.cartQty === 'plus' ? 1 : -1));
      input.value = nextQty;
      changeLine(Number(input.dataset.line), nextQty, item);
    }
  });

  document.addEventListener('focusin', (e) => {
    const input = e.target.closest('[data-cart-line-input]');
    if (input) input.dataset.value = input.value;
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-cart-line-input]');
    if (input) {
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      changeLine(Number(input.dataset.line), qty, input.closest('.cart-item'));
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(theme.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      }).catch(() => {});
    }
  });

  /* ---------------------------------------------------------------------
     Search drawer with predictive results
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-open]');
    const drawer = document.getElementById('SearchDrawer');
    if (!trigger || !drawer) return;
    e.preventDefault();
    Drawer.open(drawer, trigger);
  });

  const searchDrawer = document.getElementById('SearchDrawer');
  if (searchDrawer && searchDrawer.dataset.predictive === 'true') {
    const input = $('[data-predictive-input]', searchDrawer);
    const results = $('[data-predictive-results]', searchDrawer);
    const popular = $('[data-search-popular]', searchDrawer);
    let controller;

    const fetchResults = debounce(async (term) => {
      if (!term) { results.innerHTML = ''; if (popular) popular.hidden = false; return; }
      if (popular) popular.hidden = true;
      controller?.abort();
      controller = new AbortController();
      try {
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection,page,article',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'last',
          section_id: 'predictive-search'
        });
        const res = await fetch(`${theme.routes.predictiveSearch}?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) return;
        const doc = parseHTML(await res.text());
        const content = $('#shopify-section-predictive-search', doc) || doc.body;
        results.innerHTML = content.innerHTML;
      } catch (err) { /* aborted */ }
    }, 280);

    input?.addEventListener('input', () => fetchResults(input.value.trim()));
  }

  /* ---------------------------------------------------------------------
     Quantity selectors (product page)
     --------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-btn]');
    if (!btn) return;
    const input = $('input', btn.closest('[data-qty]'));
    const min = parseInt(input.min, 10) || 1;
    const val = parseInt(input.value, 10) || min;
    input.value = Math.max(min, val + (btn.dataset.qtyBtn === 'plus' ? 1 : -1));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------------------------------------------------------------------
     Product page: gallery, variants, sticky ATC, share
     --------------------------------------------------------------------- */
  const initProducts = (root = document) => {
    $$('[data-product]', root).forEach((product) => {
      if (product.dataset.ready) return;
      product.dataset.ready = 'true';

      const slides = $('[data-gallery-slides]', product);
      const thumbs = $$('[data-gallery-thumb]', product);
      const dots = $$('[data-gallery-dot]', product);

      const setActive = (index) => {
        thumbs.forEach((t, i) => { t.classList.toggle('is-active', i === index); t.setAttribute('aria-current', i === index ? 'true' : 'false'); });
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      };
      const goTo = (index) => {
        if (!slides) return;
        const slide = slides.children[index];
        if (slide) slides.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        setActive(index);
      };
      const currentIndex = () => (slides && slides.clientWidth ? Math.round(slides.scrollLeft / slides.clientWidth) : 0);

      if (slides) {
        slides.addEventListener('scroll', debounce(() => setActive(currentIndex()), 60), { passive: true });
        thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
        $('[data-gallery-prev]', product)?.addEventListener('click', () => goTo(Math.max(0, currentIndex() - 1)));
        $('[data-gallery-next]', product)?.addEventListener('click', () => goTo(Math.min(slides.children.length - 1, currentIndex() + 1)));
      }

      // Variants
      const variantsEl = $('[data-variants]', product);
      let variants = [];
      try { variants = variantsEl ? JSON.parse(variantsEl.textContent) : []; } catch (err) { variants = []; }
      const fieldsets = $$('[data-option-fieldset]', product);
      const idInputs = $$('[data-variant-id]', product);
      const priceEl = $('[data-product-price]', product);
      const addButtons = $$('[data-add-button]', product);
      const stickyPrice = $('[data-sticky-price]', product);
      const stockEl = $('[data-stock]', product);

      const renderPrice = (v) => {
        if (!priceEl) return;
        let html = `<div class="price${v.compare ? ' price--on-sale' : ''}${v.available ? '' : ' price--sold-out'}"><span class="price__current">${v.price}</span>`;
        if (v.compare) html += `<s class="price__compare">${v.compare}</s><span class="price__save">${(priceEl.dataset.saveLabel || '').replace('[amount]', v.save)}</span>`;
        html += '</div>';
        priceEl.innerHTML = html;
        if (stickyPrice) stickyPrice.innerHTML = v.price;
      };

      const renderStock = (v) => {
        if (!stockEl) return;
        if (!v || !v.available) { stockEl.hidden = true; return; }
        stockEl.hidden = false;
        const low = v.low !== null && v.low !== undefined;
        stockEl.classList.toggle('buy-buttons__stock--low', low);
        stockEl.textContent = low ? stockEl.dataset.lowStock.replace('[count]', v.low) : stockEl.dataset.inStock;
      };

      const setButtons = (v) => {
        addButtons.forEach((btn) => {
          const label = $('[data-add-label]', btn) || btn;
          if (!v) { btn.disabled = true; label.textContent = theme.strings.unavailable; return; }
          btn.disabled = !v.available;
          label.textContent = v.available ? theme.strings.addToCart : theme.strings.soldOut;
        });
        // The dynamic checkout button can't be disabled directly: hide it for unavailable selections
        const payment = $('.shopify-payment-button', product);
        if (payment) payment.hidden = !v || !v.available;
      };

      const selectedOptions = () => fieldsets.map((fs) => {
        const checked = $('input:checked', fs);
        return checked ? checked.value : null;
      });

      const markAvailability = (selected) => {
        fieldsets.forEach((fs, position) => {
          $$('input', fs).forEach((input) => {
            const combo = selected.slice();
            combo[position] = input.value;
            const match = variants.find((v) => v.options.every((opt, i) => opt === combo[i]));
            input.classList.toggle('is-unavailable', !match || !match.available);
          });
        });
      };

      const onOptionChange = () => {
        const selected = selectedOptions();
        fieldsets.forEach((fs, i) => {
          const legendValue = $('[data-option-value]', fs);
          if (legendValue) legendValue.textContent = selected[i] || '';
        });
        markAvailability(selected);
        const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
        setButtons(variant);
        renderStock(variant);
        if (!variant) return;
        idInputs.forEach((input) => { input.value = variant.id; });
        renderPrice(variant);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.media && slides) {
          const idx = Array.from(slides.children).findIndex((s) => s.dataset.mediaId === String(variant.media));
          if (idx > -1) goTo(idx);
        }
      };
      product.addEventListener('change', (e) => { if (e.target.closest('[data-option-fieldset]')) onOptionChange(); });
      if (fieldsets.length) markAvailability(selectedOptions());

      // Sticky add to cart
      const sticky = $('[data-sticky-atc]', product);
      const mainButtons = $('[data-main-buy-buttons]', product);
      if (sticky && mainButtons && 'IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => {
          const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          sticky.classList.toggle('is-visible', show);
          sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          $$('button', sticky).forEach((b) => { b.tabIndex = show ? 0 : -1; });
        }).observe(mainButtons);
        $('[data-sticky-submit]', sticky)?.addEventListener('click', (e) => {
          if (e.currentTarget.disabled) return;
          const form = $('form[data-product-form]', mainButtons);
          if (!form) return;
          const submitter = $('[data-add-button]', form);
          if (form.requestSubmit) form.requestSubmit(submitter || undefined);
          else submitter?.click();
        });
      }

      // Share
      $$('[data-share]', product).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const data = { title: btn.dataset.title, url: btn.dataset.url };
          if (navigator.share) { try { await navigator.share(data); } catch (err) { /* dismissed */ } return; }
          try {
            await navigator.clipboard.writeText(data.url);
            const msg = $('[data-share-message]', btn.parentElement);
            if (msg) { msg.hidden = false; setTimeout(() => { msg.hidden = true; }, 2200); }
          } catch (err) { /* clipboard unavailable */ }
        });
      });
    });
  };
  initProducts();

  /* ---------------------------------------------------------------------
     Product recommendations
     --------------------------------------------------------------------- */
  const initRecommendations = (root = document) => {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = 'true';
      const wrapper = el.closest('.shopify-section');
      try {
        const res = await fetch(el.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = $('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        } else if (wrapper) wrapper.hidden = true;
      } catch (err) {
        if (wrapper) wrapper.hidden = true;
      }
    });
  };
  initRecommendations();

  /* ---------------------------------------------------------------------
     Collection filters & sorting (auto-submit)
     --------------------------------------------------------------------- */
  const facetForm = $('[data-facet-form]');
  if (facetForm) {
    const submit = debounce(() => {
      const params = new URLSearchParams(new FormData(facetForm));
      for (const [key, value] of Array.from(params.entries())) { if (value === '') params.delete(key); }
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    }, 450);
    document.addEventListener('change', (e) => {
      if (e.target.form === facetForm) submit();
    });
    facetForm.addEventListener('submit', (e) => { e.preventDefault(); submit(); });
  }

  /* ---------------------------------------------------------------------
     Theme editor support
     --------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initSliders(e.target);
    initAnnouncements(e.target);
    initHoverMenus(e.target);
    initProducts(e.target);
    initRecommendations(e.target);
    syncStaticDrawers();
    syncFooterAccordions();
    setHeaderOffset();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (Drawer.active && e.target.contains(Drawer.active)) Drawer.close(Drawer.active, false);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const item = e.target.closest('.announcement__item');
    if (item) {
      $$('.announcement__item', item.parentElement).forEach((el) => el.classList.toggle('is-active', el === item));
    }
    const slide = e.target.closest('[data-slider-track] > *');
    if (slide) slide.parentElement.scrollTo({ left: slide.offsetLeft - slide.parentElement.offsetLeft, behavior: 'smooth' });
  });
})();
